import React, { useMemo, useRef } from 'react';
import { Listbox } from '@headlessui/react';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import 'flag-icons/css/flag-icons.min.css';

export default function PhoneCountrySelect({ value, onChange, className = '' }) {
  const countries = useMemo(() => {
    try {
      const display = new Intl.DisplayNames(['en'], { type: 'region' });
      return getCountries()
        .map((code) => ({
          code,
          dial: `+${getCountryCallingCode(code)}`,
          name: display ? display.of(code) : code,
        }))
        .sort((a, b) => (a.name || a.code).localeCompare(b.name || b.code));
    } catch {
      return getCountries()
        .map((code) => ({ code, dial: `+${getCountryCallingCode(code)}`, name: code }))
        .sort((a, b) => a.code.localeCompare(b.code));
    }
  }, []);

  const selected = useMemo(() => {
    return countries.find((c) => c.code === value) || countries.find((c) => c.code === 'US') || countries[0];
  }, [countries, value]);

  // Simple type-ahead: type a country name to jump/select (without a visible search box)
  const typeAheadRef = useRef({ query: '', timer: null });
  const handleKeyDown = (e) => {
    const isChar = e.key && e.key.length === 1 && /[a-zA-Z\s]/.test(e.key);
    const isBackspace = e.key === 'Backspace';
    if (!isChar && !isBackspace) return;
    e.stopPropagation();
    const state = typeAheadRef.current;
    // reset timer
    if (state.timer) clearTimeout(state.timer);
    if (isBackspace) {
      state.query = state.query.slice(0, -1);
    } else {
      state.query += e.key.toLowerCase();
    }
    state.timer = setTimeout(() => {
      typeAheadRef.current.query = '';
      typeAheadRef.current.timer = null;
    }, 800);
    const q = state.query.trim();
    if (!q) return;
    const next = countries.find((c) => (c.name || c.code).toLowerCase().startsWith(q));
    if (next && typeof onChange === 'function') onChange(next.code);
  };

  return (
    <Listbox value={selected?.code} onChange={onChange}>
      <div className={`relative ${className}`} tabIndex={0} onKeyDown={handleKeyDown}>
        <Listbox.Button className="w-full pl-10 pr-8 py-2.5 text-left bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center">
            <span className={`fi fi-${(selected?.code || 'us').toLowerCase()} rounded-sm`}></span>
          </span>
          <span className="block truncate text-gray-900 dark:text-gray-100">{selected?.dial} ({selected?.code})</span>
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
            <svg className="h-4 w-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/></svg>
          </span>
        </Listbox.Button>
        <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {countries.map((c) => (
            <Listbox.Option
              key={c.code}
              value={c.code}
              className={({ active }) => `${active ? 'bg-indigo-100 dark:bg-gray-700 text-indigo-900 dark:text-gray-100' : 'text-gray-900 dark:text-gray-100'} relative cursor-default select-none py-2 pl-10 pr-4`}
            >
              {({ selected: isSelected }) => (
                <>
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex items-center">
                    <span className={`fi fi-${c.code.toLowerCase()} rounded-sm`}></span>
                  </span>
                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>{c.dial} ({c.code})</span>
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  );
}


